import { LastUpdate } from "components/icons/icn-last-update.icons";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import intl from "react-intl-universal";
import { LastUpdateTooltip, LastUpdateTrigger } from "./styled";

type TooltipPosition = {
  left: number;
  top: number;
};

type Props = Readonly<{
  value: string;
}>;

const formatLastUpdate = (lastUpdate: string) => moment(lastUpdate).format("DD/MM/YYYY hh:mm A");

const LastUpdateCell = ({ value }: Props) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const formattedLastUpdate = formatLastUpdate(value);
  const lastUpdateLabel = intl.get("productionPlanSupplier.table.lastUpdate");

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const updateTooltipPosition = useCallback(() => {
    const triggerElement = triggerRef.current;

    if (!triggerElement) {
      return;
    }

    const rect = triggerElement.getBoundingClientRect();

    setTooltipPosition({
      left: rect.left - 8,
      top: rect.top + rect.height / 2,
    });
  }, []);

  const showTooltip = () => {
    clearHideTimer();
    updateTooltipPosition();
    setIsTooltipVisible(true);
  };

  const hideTooltip = () => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      setIsTooltipVisible(false);
    }, 120);
  };

  useEffect(() => {
    if (!isTooltipVisible) {
      return undefined;
    }

    const handlePositionChange = () => {
      updateTooltipPosition();
    };

    window.addEventListener("resize", handlePositionChange);
    window.addEventListener("scroll", handlePositionChange, true);

    return () => {
      window.removeEventListener("resize", handlePositionChange);
      window.removeEventListener("scroll", handlePositionChange, true);
    };
  }, [isTooltipVisible, updateTooltipPosition]);

  useEffect(
    () => () => {
      clearHideTimer();
    },
    [clearHideTimer]
  );

  return (
    <LastUpdateTrigger
      innerRef={(node: HTMLButtonElement | null) => {
        triggerRef.current = node;
      }}
      type="button"
      aria-label={`${lastUpdateLabel}: ${formattedLastUpdate}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      <LastUpdate width="12px" height="12px" />
      {isTooltipVisible && tooltipPosition && typeof document !== "undefined"
        ? createPortal(
            <LastUpdateTooltip
              role="tooltip"
              style={{
                left: tooltipPosition.left,
                top: tooltipPosition.top,
              }}
            >
              <span>{lastUpdateLabel}</span>
              <span>{formattedLastUpdate}</span>
            </LastUpdateTooltip>,
            document.body
          )
        : null}
    </LastUpdateTrigger>
  );
};

export default LastUpdateCell;
